/**
 * Utilidades para calcular prorrateo de precios de suscripciones
 */

export interface ProrrateCalculationParams {
  price: number;
  dateExpire: string;
  typePeriodicity: 'MENSUAL' | 'ANUAL';
}

export interface ProrrateResult {
  prorratedPrice: number;
  dailyRate: number;
  remainingDays: number;
  totalDaysInPeriod: number;
}

/**
 * Calcula el prorrateo de un precio basado en la fecha de expiración y la recurrencia
 * @param params - Parámetros para el cálculo del prorrateo
 * @returns Resultado del cálculo de prorrateo
 */
export const calculateProrrate = (params: ProrrateCalculationParams): ProrrateResult => {
  const { price, dateExpire, typePeriodicity } = params;
  
  // Convertir la fecha de expiración a Date
  const expireDate = new Date(dateExpire);
  const currentDate = new Date();
  
  // Calcular días restantes hasta la expiración
  const timeDiff = expireDate.getTime() - currentDate.getTime();
  const remainingDays = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
  
  // Determinar el número total de días en el período según la recurrencia
  let totalDaysInPeriod: number;
  
  if (typePeriodicity === 'MENSUAL') {
    // Para mensual, usar 30 días por defecto, pero podríamos ser más precisos
    // considerando el mes actual
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const daysInCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    totalDaysInPeriod = daysInCurrentMonth;
  } else if (typePeriodicity === 'ANUAL') {
    // Para anual, usar 365 días (podríamos considerar años bisiestos)
    const currentYear = currentDate.getFullYear();
    const isLeapYear = (currentYear % 4 === 0 && currentYear % 100 !== 0) || (currentYear % 400 === 0);
    totalDaysInPeriod = isLeapYear ? 366 : 365;
  } else {
    throw new Error(`Tipo de periodicidad no soportado: ${typePeriodicity}`);
  }
  
  // Calcular el precio por día
  const dailyRate = price / totalDaysInPeriod;
  
  // Calcular el precio prorrateado
  const prorratedPrice = dailyRate * remainingDays;
  
  return {
    prorratedPrice: Math.round(prorratedPrice * 100) / 100, // Redondear a 2 decimales
    dailyRate: Math.round(dailyRate * 100) / 100,
    remainingDays,
    totalDaysInPeriod,
  };
};

/**
 * Calcula el prorrateo para precios adicionales (addons)
 * @param additionalPrice - Precio adicional por unidad
 * @param quantity - Cantidad de unidades
 * @param maxAdditional - Cantidad máxima para precio adicional
 * @param basePrice - Precio base por unidad
 * @param dateExpire - Fecha de expiración
 * @param typePeriodicity - Tipo de periodicidad
 * @returns Precio prorrateado para el addon
 */
export const calculateAddonProrrate = (
  additionalPrice: number,
  quantity: number,
  maxAdditional: number,
  basePrice: number,
  dateExpire: string,
  typePeriodicity: 'MENSUAL' | 'ANUAL'
): number => {
  // Determinar qué precio usar según la cantidad
  const pricePerUnit = quantity >= maxAdditional ? additionalPrice : basePrice;
  const totalPrice = pricePerUnit * quantity;
  
  // Aplicar prorrateo
  const prorrateResult = calculateProrrate({
    price: totalPrice,
    dateExpire,
    typePeriodicity,
  });
  
  return prorrateResult.prorratedPrice;
};

/**
 * Formatea un precio como moneda
 * @param amount - Cantidad a formatear
 * @returns Precio formateado como moneda USD
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};
